import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import {
  useAllocateEnterpriseContractorTokenMutation,
  useDeallocateEnterpriseContractorTokenMutation,
} from "store/api/enterpriseApi"
import { StorageKeys } from "types/enums/StorageKeys"

import { getStorageItem, setStorageItem } from "utils/localStorage"
import { useContractors } from "hooks/useContractors"
import { useEnterpriseMasterAccountSwitch } from "hooks/useEnterpriseMasterAccountSwitch"
import { useLogin } from "hooks/useLogin"
import { useUserProfile } from "hooks/useUserProfile"

type MenuContractor = {
  id: number
  name: string
}

export const useAccountSwitcher = () => {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [selectedContractorId, setSelectedContractorId] = useState<number | null>(null)
  const [isDropdownOpen, setDropdownOpen] = useState(false)
  const [allocateEnterpriseContractorToken] = useAllocateEnterpriseContractorTokenMutation()
  const [deallocateEnterpriseContractorToken] = useDeallocateEnterpriseContractorTokenMutation()
  const { enterpriseMasterAccountToken, hasEnterpriseMasterAccountToken, switchBackToEnterpriseMasterAccount } =
    useEnterpriseMasterAccountSwitch()
  const { login, getCurrentAuthToken } = useLogin()
  const { userProfile, isEnterprise, enterpriseContractorIds } = useUserProfile()
  const { contractors: fetchedContractors } = useContractors(enterpriseContractorIds)

  const contractors = useMemo<MenuContractor[]>(() => {
    const cachedContractors = getStorageItem(StorageKeys.EnterpriseContractorsMenuDropdown) ?? []

    return fetchedContractors.length > 0
      ? fetchedContractors.map(contractor => ({ id: contractor.id, name: contractor.name }))
      : cachedContractors
  }, [fetchedContractors])

  const selectedContractor = contractors.find(contractor => contractor.id === selectedContractorId)
  const selectedAccountLabel = selectedContractor?.name || "Master Account"
  const showsAccountSwitcher = isEnterprise || hasEnterpriseMasterAccountToken
  const isMasterAccountSelected = !selectedContractor && isEnterprise

  const syncSelectedContractor = useCallback(() => {
    const currentContractorId = userProfile?.contractor?.id
    if (currentContractorId) {
      setSelectedContractorId(currentContractorId)
      return
    }

    if (isEnterprise) {
      setSelectedContractorId(null)
    }
  }, [isEnterprise, userProfile?.contractor?.id])

  const deallocateContractorToken = useCallback(
    async (contractorId: number | null) => {
      const authToken = enterpriseMasterAccountToken
      const currentToken = getCurrentAuthToken()
      const tokenId = Number(currentToken?.split("|")?.[0] ?? "0")

      if (!contractorId || !tokenId || !authToken) return

      try {
        await deallocateEnterpriseContractorToken({ contractorId, tokenId, authToken }).unwrap()
      } catch (error) {
        console.error("Failed to deallocate contractor token", error)
      }
    },
    [deallocateEnterpriseContractorToken, enterpriseMasterAccountToken, getCurrentAuthToken],
  )

  const handleAccountClick = useCallback(
    async (contractorId: number | null) => {
      if (!isEnterprise && !contractorId) {
        await deallocateContractorToken(selectedContractorId)
        await switchBackToEnterpriseMasterAccount()
        return
      }

      if (contractorId === selectedContractorId) return

      if (contractorId) {
        const masterAccountToken = enterpriseMasterAccountToken || getCurrentAuthToken()
        const tokenAllocation = await allocateEnterpriseContractorToken({
          contractorId,
          authToken: masterAccountToken,
        }).unwrap()

        if (!isEnterprise) {
          await deallocateContractorToken(selectedContractorId)
        }

        await login({
          accessToken: tokenAllocation.data.token,
          masterToken: masterAccountToken,
          clearUserProfile: true,
        })
        setSelectedContractorId(contractorId)
        setDropdownOpen(false)
      }
    },
    [
      allocateEnterpriseContractorToken,
      deallocateContractorToken,
      enterpriseMasterAccountToken,
      getCurrentAuthToken,
      isEnterprise,
      login,
      selectedContractorId,
      switchBackToEnterpriseMasterAccount,
    ],
  )

  useEffect(() => {
    syncSelectedContractor()
  }, [syncSelectedContractor])

  useEffect(() => {
    if (isEnterprise) {
      setStorageItem(StorageKeys.EnterpriseContractorsMenuDropdown, contractors)
    }
  }, [isEnterprise, contractors])

  useEffect(() => {
    if (!showsAccountSwitcher) {
      return
    }

    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) {
        setDropdownOpen(false)
      }
    }

    document.addEventListener("mousedown", closeOnOutsideClick)

    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick)
    }
  }, [showsAccountSwitcher])

  return {
    contractors,
    handleAccountClick,
    isDropdownOpen,
    isMasterAccountSelected,
    selectedAccountLabel,
    selectedContractorId,
    setDropdownOpen,
    showsAccountSwitcher,
    wrapperRef,
  }
}
